import React, { useState, useEffect } from 'react';
import { signInWithPopup, signInWithRedirect, getRedirectResult } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { auth, googleProvider } from '../firebase';  // Fix imports
import './GoogleSignUp.css';

const GoogleSignUp = () => {
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [isInAppBrowser, setIsInAppBrowser] = useState(false);
    const [copied, setCopied] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [showEmulatorLink, setShowEmulatorLink] = useState(false);
    const navigate = useNavigate();

    // Detect if user is on mobile or accessing via IP address
    useEffect(() => {
        const userAgent = navigator.userAgent || '';
        const isInstagram = userAgent.includes('Instagram');
        const isFacebook = userAgent.includes('FBAN') || userAgent.includes('FBAV');
        const isSnapchat = userAgent.includes('Snapchat');
        const isTikTok = userAgent.includes('TikTok') || userAgent.includes('BytedanceWebview');
        const isTwitter = userAgent.includes('Twitter') || userAgent.includes('X Browser');
        
        setIsInAppBrowser(isInstagram || isFacebook || isSnapchat || isTikTok || isTwitter);
        
        // Detect mobile device or IP address access (for mobile testing)
        const isMobileDevice = /iPhone|iPad|iPod|Android/i.test(userAgent);
        const isIPAddress = /^(\d{1,3}\.){3}\d{1,3}$/.test(window.location.hostname) && window.location.hostname !== '127.0.0.1';
        setIsMobile(isMobileDevice || isIPAddress);
        
        // Check for redirect result when component mounts (user returning from OAuth redirect)
        const handleRedirectResult = async () => {
            try {
                const result = await getRedirectResult(auth);
                if (result) {
                    setLoading(true);
                    // User successfully signed in via redirect
                    setTimeout(() => {
                        navigate('/auth-home');
                    }, 1000);
                }
            } catch (error) {
                console.error("Error handling redirect result:", error);
                setError(error.message);
                setLoading(false);
            }
        };
        
        handleRedirectResult();
    }, [navigate]);

    const handleGoogleSignIn = async () => {
        try {
            setLoading(true);
            setError('');
            setShowEmulatorLink(false);
            
            // Check if we're using the Auth emulator
            const isUsingEmulator = process.env.REACT_APP_USE_EMULATORS !== 'false' && 
                                    process.env.NODE_ENV === 'development';
            
            // Force correct emulator host based on current page hostname
            // This ensures we use the correct IP even if Firebase cached an old one
            const currentHostname = window.location.hostname;
            const expectedEmulatorHost = currentHostname === 'localhost' || currentHostname === '127.0.0.1' 
                ? 'localhost' 
                : currentHostname;
            
            if (isUsingEmulator) {
                console.log(`🔍 Current hostname: ${currentHostname}`);
                console.log(`🔍 Expected emulator host: ${expectedEmulatorHost}`);
                console.log(`🔍 Emulator should be at: http://${expectedEmulatorHost}:9099`);
                console.log(`🔍 If you see 192.168.1.205, clear browser cache and restart!`);
            }
            
            // Desktop: Always use popup (works perfectly)
            // Mobile: Try popup first, if blocked show direct link to emulator
            if (!isMobile) {
                // Desktop: Use popup flow
                const result = await signInWithPopup(auth, googleProvider);
                
                // Success! Navigate to home
                setTimeout(() => {
                    navigate('/auth-home');
                }, 2000);
            } else {
                // Mobile: Try popup first, fallback to direct link
                try {
                    const result = await signInWithPopup(auth, googleProvider);
                    
                    // Success! Navigate to home
                    setTimeout(() => {
                        navigate('/auth-home');
                    }, 2000);
                } catch (popupError) {
                    console.error('❌ Popup error:', popupError.code, popupError.message);
                    // If popup is blocked on mobile, show direct link to emulator UI
                    if (popupError.code === 'auth/popup-blocked' && isUsingEmulator) {
                        setShowEmulatorLink(true);
                        setError('Popup blocked. Tap the link below to sign in with the Auth Emulator UI:');
                        setLoading(false);
                    } else {
                        setError(popupError.message || 'Failed to sign in. Please try again.');
                        setLoading(false);
                    }
                }
            }
            
        } catch (error) {
            console.error('❌ Sign-in error:', error);
            setError(error.message || 'Failed to sign in. Please try again.');
            setLoading(false);
        }
    };
    
    const copyToClipboard = () => {
        const url = window.location.origin;
        navigator.clipboard.writeText(url)
            .then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 2000); // Reset copied state after 2 seconds
            })
            .catch(err => {
                console.error('Failed to copy: ', err);
            });
    };

    return (
        <div className="google-signup-container">
            <h2>Sign up/in with Google</h2>
            {error && <p className="error">{error}</p>}
            
            {showEmulatorLink && !loading && (
                <div className="emulator-link-container" style={{ marginTop: '20px', padding: '15px', backgroundColor: '#f0f0f0', borderRadius: '5px', textAlign: 'center' }}>
                    <p style={{ marginBottom: '15px', fontWeight: 'bold' }}>Mobile Testing Alternative:</p>
                    <p style={{ marginBottom: '15px', fontSize: '0.95em' }}>
                        On mobile devices, popups are often blocked. Use the Firebase Emulator UI to sign in:
                    </p>
                    <a 
                        href={`http://${window.location.hostname}:4000`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ 
                            display: 'inline-block',
                            padding: '12px 24px',
                            backgroundColor: '#4285f4',
                            color: 'white',
                            textDecoration: 'none',
                            borderRadius: '5px',
                            fontWeight: 'bold',
                            fontSize: '1.1em',
                            marginBottom: '10px'
                        }}
                    >
                        Open Firebase Emulator UI
                    </a>
                    <p style={{ marginTop: '15px', fontSize: '0.85em', color: '#666' }}>
                        After signing in the Emulator UI, return here. Your session will be shared.
                    </p>
                    <p style={{ marginTop: '10px', fontSize: '0.85em', color: '#666' }}>
                        Alternatively, test on your computer's localhost:3000 where popup works perfectly.
                    </p>
                </div>
            )}
            
            {isInAppBrowser && !loading && (
                <div className="browser-warning">
                    <p>
                        You're viewing from an in-app browser, which may prevent Google Sign-In from working correctly.
                    </p>
                    <p>
                        For the best experience, please copy this link and open it in Safari, Chrome, or another browser:
                    </p>
                    <div className="copy-url-container">
                        <span className="url-text">{window.location.origin}</span>
                        <button 
                            className="copy-button"
                            onClick={copyToClipboard}
                        >
                            {copied ? '✓ Copied!' : 'Copy Link'}
                        </button>
                    </div>
                </div>
            )}
            
            {loading ? (
                <div className="auth-loading">
                    <div className="loading-spinner"></div>
                    <p>Signing in and preparing your experience...</p>
                </div>
            ) : (
                <>
                    <button 
                        onClick={handleGoogleSignIn}
                        className="google-button"
                        disabled={loading}
                    >
                        Continue with Google
                    </button>

                    <button 
                        onClick={() => navigate('/')}
                        className="back-button"
                        disabled={loading}
                    >
                        Back to Home
                    </button>
                </>
            )}
        </div>
    );
};

export default GoogleSignUp;